import {useIsFeatureActive} from '@aboutyou/feature-flags';
import {Coupon_HotDealCoupon_ProductAndBrandInformation} from '@aboutyou/grpc-clients/tadarida/aysa_api/models/coupon.types';
import {Product} from '@aboutyou/grpc-clients/tadarida/aysa_api/models/product.types';
import {useTranslation} from '@aboutyou/i18n';
import {useHistory} from '@aboutyou/router';
import {
  BrandLogo,
  Button,
  COLORS,
  FONT_WEIGHTS,
  GenericIconName,
  Icon,
  PriceBox,
  PriceBoxFinalPrice,
  PriceBoxPriceAndReductionsInline,
  PriceBoxSalesRow,
  ProductImage,
  Text,
} from '@aboutyou/ui-components';
import {useIsSizeInBasket} from '@aboutyou/user';
import {styled as linariaStyled} from '@linaria/react';
import React, {FC, useEffect} from 'react';
import {
  cancelAnimationFrame,
  requestAnimationFrame,
} from 'react-viewport-utils';
import sc from 'styled-components';
import {useWalletStateManager} from '../../../backend-services/Wallet/utils/useWalletStateManager';
import {useSizeSelectionOpenedStateManager} from '../../Coupons/CouponWallet/hooks/useSizeSelectionOpenedStateManager';
import {useProductIdFromSlug} from '../../ProductUrl';
import {DownloadAppButton} from './DownloadAppButton';
import {DownloadAppQr, DownloadText, QRCode, ScanQRText} from './DownloadAppQr';

export const ProductBox: FC<
  Coupon_HotDealCoupon_ProductAndBrandInformation & {
    isOnlyAppCoupon?: boolean;
    isDisabled?: boolean;
    supportsHover?: boolean;
    setSelectedProduct?: (product: Product | undefined) => void;
    activateCouponOnAddToBasket?: VoidFunction;
  }
> = ({
  product,
  price,
  priceV2,
  brand,
  isOnlyAppCoupon,
  isDisabled,
  supportsHover,
  setSelectedProduct,
  activateCouponOnAddToBasket,
}) => {
  const {t} = useTranslation();
  const {isActive: isPriceDisplayOverhaulV2Active} = useIsFeatureActive(
    'ab_price_display_overhaul_v2',
  );
  return (
    <ProductSummary
      data-disabled={Boolean(isDisabled)}
      data-testid="productBox"
    >
      <ProductImageWrapper>
        <StyledProductImage
          src={product.primaryBustImage.src}
          widths={[120, 240, 360]}
          ratio="1:1"
        />
        {product.isSoldOut && (
          <Layout>
            <SoldOutText size="px16" weight="mediumHeavy" color="White">
              {t('Sold Out')}
            </SoldOutText>
          </Layout>
        )}
      </ProductImageWrapper>
      <ProductInfo>
        <StyledBrandLogo
          name={brand.name}
          showTooltip={false}
          image={brand.blackLogoImage}
          maxHeight="24px"
          maxWidth="80px"
        />
        <StyledPriceBox
          price={isPriceDisplayOverhaulV2Active ? priceV2 : price}
          showReductionsInline
          hideVat
          hideFromLabel
          showSmallerFont
          showReductionsLargeFont
          hideLpl30
          hideBadges
          reverseReductions
          productId={product.id}
        />
        <AddToBasketButtonOrQrCode
          isOnlyAppCoupon={isOnlyAppCoupon}
          isDisabled={isDisabled}
          isSoldOut={product.isSoldOut}
          product={product}
          supportsHover={supportsHover}
          setSelectedProduct={setSelectedProduct}
          activateCouponOnAddToBasket={activateCouponOnAddToBasket}
        />
      </ProductInfo>
    </ProductSummary>
  );
};

interface AddToBasketButtonOrQrCodeProps {
  isOnlyAppCoupon?: boolean;
  isDisabled?: boolean;
  isSoldOut?: boolean;
  product: Product;
  supportsHover?: boolean;
  'data-testid'?: string;
  setSelectedProduct?: (product: Product | undefined) => void;
  activateCouponOnAddToBasket?: VoidFunction;
}

const AddToBasketButtonOrQrCode: FC<AddToBasketButtonOrQrCodeProps> = ({
  isOnlyAppCoupon,
  isDisabled,
  isSoldOut,
  product,
  supportsHover,
  'data-testid': dataTestId,
  setSelectedProduct,
  activateCouponOnAddToBasket,
}) => {
  const {t} = useTranslation();
  const isInBasket = useIsSizeInBasket();
  const isAnyProductOnTheBasket = product.sizes.some(size =>
    isInBasket(size.id),
  );

  /**
   * For adding to the basket using the Coupon wallet, we use the Mini ADP on
   * desktop and the Size Selector on mWeb, setSelectedProduct is used to set
   * the product for the size selection Flyout therefore, if this validation
   * is false, then the coupon wallet will use the Mini ADP
   * */
  const isMiniADP = !setSelectedProduct;

  const productIdFromSlug = useProductIdFromSlug();
  const isSameADPProduct = productIdFromSlug === product.id;
  const {push} = useHistory();

  const {cancel} = useWalletStateManager();
  const {open: openAddToBasketComponent} = useSizeSelectionOpenedStateManager({
    isMiniADP,
  });

  let animationFrameId = NaN;

  useEffect(() => {
    return cancelAnimationFrame(animationFrameId);
  }, []);

  const handleOnClick = (e: React.MouseEvent<Element, MouseEvent>) => {
    e.stopPropagation();
    activateCouponOnAddToBasket?.();

    /**
     * When a user wants to add a product form the coupon wallet and they are on the same ADP,
     * we want to prevent opening the mini ADP for user experience, and instead, close the coupon
     * wallet, so the user can use the regular ADP. We navigate to the ADP, since it'll close the
     * offcanvas on mWeb as well, it's an easier approach
     * */
    if (isSameADPProduct) {
      push(product.path);
    } else {
      // On desktop we want to close the wallet when the add to basket button is clicked to avoid
      // having 2 Drawers opened when the success add to basket component is opened
      if (isMiniADP) {
        animationFrameId = requestAnimationFrame(cancel);
      }
      // need setTimeout to prevent closing the mini ADP, since we'll need to close the wallet, and the 2 changes
      // on the open state manager will clash
      setTimeout(() => {
        openAddToBasketComponent({productId: product.id});
        setSelectedProduct?.(product);
      }, 500);
    }
  };

  if (isOnlyAppCoupon) {
    if (supportsHover) {
      return <StyledDownloadAppQr isDisabled={isDisabled} />;
    }

    return (
      <DownloadAppButton
        data-testid={`${dataTestId}DownloadButton`}
        $isDisabled={isDisabled}
      />
    );
  }

  if (isSoldOut) {
    return null;
  }

  return (
    <StyledAddButton
      variant="secondary"
      onClick={handleOnClick}
      data-testid="addToBasketButton"
      aria-label={undefined}
      disabled={Boolean(isDisabled)}
    >
      <Icon
        asset={
          isAnyProductOnTheBasket
            ? GenericIconName.AddToBasketWithCheckMark
            : GenericIconName.AddToBasket
        }
        size="SS"
      />
      {isAnyProductOnTheBasket ? t('Go to basket') : t('Add to basket')}
    </StyledAddButton>
  );
};

const ProductSummary = sc.div`
  display: flex;
  background-color: ${COLORS.White};
  padding: 14px;
  position: relative;
`;

const ProductImageWrapper = sc.div`
  display: flex;
  flex: 1;
  align-items: center;
  padding: 12px 22px;
  background-color: ${COLORS.ProductImageBackground};
  border-radius: 4px;
  position: relative;
  overflow: hidden;
  aspect-ratio: 1/1;
  max-width: 109px;
`;

const ProductInfo = sc.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding-left: 16px;
  flex: 1;
`;

const StyledProductImage = sc(ProductImage)`
  > img {
    object-fit: contain;
  }

  [data-disabled='true'] & {
    filter: grayscale(1);
    opacity: 0.6;
  }
`;

const StyledBrandLogo = sc(BrandLogo)`
  height: auto;
  width: 80px;

  [data-disabled='true'] & {
    filter: grayscale(1);
    opacity: 0.6;
  }
`;

const Layout = sc.div`
  align-items: center;
  background-color: rgba(0, 0, 0, 0.4);
  display: flex;
  inset: 0;
  justify-content: center;
  padding-inline: 16px;
  position: absolute;
`;

const SoldOutText = sc(Text)`
  line-height: 1.6;
`;

const StyledAddButton = sc(Button)`
  white-space: nowrap;
`;

const StyledPriceBox = linariaStyled(PriceBox)`
  [data-disabled='true'] & {
    filter: grayscale(1);
    opacity: 0.6;
  }

  ${PriceBoxFinalPrice} {
    line-height: 1.5;
    font-weight: ${FONT_WEIGHTS.mediumBold};
    margin: 0;
  }

  ${PriceBoxSalesRow} {
    transform: initial;
  }

  ${PriceBoxPriceAndReductionsInline} {
    flex-wrap: wrap;
  }
`;

const StyledDownloadAppQr = sc(DownloadAppQr)`
  --transition-duration: 500ms;
  position: absolute;
  right: 0;
  bottom: 0;
  height: 24px;

  width: 180px;

  // eslint-disable-next-line local-rules/css-layout-animation
  transition-property: height, background-color, transform, width;
  transition-duration: var(--transition-duration);
  transition-timing-function: ease-in-out;

  ${ScanQRText} {
    transition-property: width, transform, opacity;
    transition-duration: var(--transition-duration);
    transition-timing-function: ease-in-out;
  }

  ${DownloadText} {
    padding: 0 14px 24px 0;

    transition-property: opacity, transform;
    transition-duration: calc(var(--transition-duration) + 110ms);
    transition-timing-function: ease-in-out;
  }

  ${QRCode} {
    transform: translateY(-13px);
  }

  :hover {
    background-color: ${COLORS.White};
    height: 100%;
    width: 100%;

    ${QRCode} {
      height: 68px;
      position: absolute;
      right: calc(100% - 68px - 35px);
      transform: translateY(0px);
    }

    ${DownloadText} {
      opacity: 0;
      transform: translate(-90%, -90%);
    }

    ${ScanQRText} {
      transform: translate(0%, 0%);
      opacity: 1;
      width: 68%;
    }
  }
`;
